import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import treeStructureSvg from 'assets/icons/tree-structure.svg';
import { FeatureFlags } from 'components/common/types/Features.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import usePendoTracking from 'customHooks/usePendoTracking';

import ToggleSwitch from 'lib/ToggleSwitch';
import SvgIcon from 'lib/SvgIcon';
import { Color } from 'lib/SvgIcon/SvgIcon.types';
import { endDealViewSwitch } from 'utils/dealSwitchView';
import { getPlannerFamily } from 'utils/isPlannerPath';
import { hasPlannerOnlyFilters } from 'utils/hasPlannerOnlyFilters';
import Modal from 'lib/Modal';
import { getBackupLines } from 'store/dealManagement/selectors/getBackupLines';
import { getCurrentLine } from 'store/dealManagement/selectors/getCurrentLine';
import { getToggleLabel } from './getToggleLabel';
import { switchDealView } from './switchDealView';

const DealViewToggle = (): React.ReactElement | null => {
  const hasOnlyAllowPlanner = useHasFeatureAccess(FeatureFlags.ONLY_ALLOW_PLANNER);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { getPendoDataId } = usePendoTracking();
  const { pathname, state: locationState } = useLocation();
  const navigate = useNavigate();
  const { campaignId, internalId } = useParams();

  const lines = useSelector(getBackupLines);
  const currentLine = useSelector(getCurrentLine);
  const openedForPlannerOnlyFilters = Boolean(
    (locationState as { hasPlannerOnlyFilters?: boolean } | null)?.hasPlannerOnlyFilters,
  );

  useEffect(() => {
    endDealViewSwitch();
  }, []);

  if (hasOnlyAllowPlanner) return null;

  const isPlanner = getPlannerFamily(pathname) === 'programmatic';
  const isDealForm = pathname === '/deal' || pathname.startsWith('/deal/');

  const isBlocked = isPlanner && hasPlannerOnlyFilters(lines, currentLine);

  if (!isPlanner && !isDealForm) return null;

  const id = campaignId || internalId;
  const label = getToggleLabel(isPlanner);

  const switchView = (): void => {
    switchDealView({
      isBlocked,
      isPlanner,
      id,
      navigate,
      openModal: () => setIsModalOpen(true),
    });
  };

  const closeModal = (): void => {
    setIsModalOpen(false);

    if (openedForPlannerOnlyFilters) {
      navigate(pathname, { replace: true, state: null });
    }
  };

  return (
    <div
      data-test-id="deal-view-toggle"
      className="flex min-w-0 items-center gap-1 py-1 px-2 bg-neutral-50 rounded-[12px] shadow-sm"
    >
      <span className="sub-header-base flex-1 text-neutral-950 flex items-center gap-1" title={label}>
        <SvgIcon color={Color.SECONDARY} src={treeStructureSvg} /> {label}
      </span>
      <ToggleSwitch onChange={switchView} dataPendoId={getPendoDataId(label)} ddActionName={label} />
      <Modal
        isOpen={isModalOpen || openedForPlannerOnlyFilters}
        onClose={closeModal}
        title="Can't switch to the deal form"
        text="This deal cannot be opened in the legacy deal form, as it uses features the form cannot edit."
      />
    </div>
  );
};

export default DealViewToggle;
